import TestCreatePanel from '@boViews/shared/TestCreatePanel';
import FlexBox from '@sharedComponents/FlexBox';
import Sidebar from '@sharedComponents/Sidebar';
import Typography from '@sharedComponents/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TestSearch from '../TestSearch';
import TestTable from '../TestTable';

/**
 * "Tests" tab of the collection page: its header counts the `tests` of the collection, then the search that adds
 * one (see TestSearch, fed by `onSearch` and `onAdd`) and the table of the tests (see TestTable, fed by `onRemove`
 * and `pendingReferences`). `languagesOptions` are the catalogue languages (their id labelled by their code) and
 * `levelsOptions` the levels of the tests. When the search finds nothing, a side panel creates the test (see
 * TestCreatePanel): `onCreate(payload)` resolves once the test is created, which closes the panel and searches
 * again the current term, and rejects to keep the panel open. Once the last test is removed, the focus moves to
 * the search field.
 */
const TestsTab = ({
    tests = [],
    languagesOptions = [],
    levelsOptions = [],
    onSearch,
    onAdd,
    onCreate = async () => {},
    onRemove,
    pendingReferences = [],
}) => {
    const { t } = useTranslation('bo');
    const searchRef = useRef(null);
    // Reference the panel opens with, null while it is closed.
    const [creatingReference, setCreatingReference] = useState(null);
    // Bumped once a test is created, so that the search shows it.
    const [revision, setRevision] = useState(0);

    const close = () => {
        setCreatingReference(null);
        searchRef.current?.focus();
    };

    const create = async (payload) => {
        await onCreate(payload);
        close();
        setRevision((current) => current + 1);
    };

    return (
        <FlexBox flexDirection="column" gap="s">
            <Typography seo={2} fontSize="l" fontWeight="bold" color="neutral-primary">
                {t('Tests ({{total}})', { total: tests.length })}
            </Typography>
            <TestSearch
                ref={searchRef}
                collectionTests={tests}
                languagesOptions={languagesOptions}
                onSearch={onSearch}
                onAdd={onAdd}
                onCreate={setCreatingReference}
                revision={revision}
            />
            <TestTable
                tests={tests}
                languagesOptions={languagesOptions}
                onRemove={onRemove}
                onEmptied={() => searchRef.current?.focus()}
                pendingReferences={pendingReferences}
            />
            <Sidebar isOpened={creatingReference !== null} onClose={close} title={t('New test')}>
                {creatingReference !== null && (
                    <TestCreatePanel
                        initialReference={creatingReference}
                        languagesOptions={languagesOptions}
                        levelsOptions={levelsOptions}
                        onSubmit={create}
                    />
                )}
            </Sidebar>
        </FlexBox>
    );
};

export default TestsTab;
